import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDeleteAdminUser, useToggleAccountStatus, useCurrentUserProfile } from '@/lib/api/hooks/admin-user';
import { useInviteAdminUser, useCurrentUser } from '@/lib/api/hooks/auth';
// import type { AdminUser } from '@/lib/api/types';
import { Button, ActionDropdown, TanStackTable, type ColumnDef, type DropdownAction } from '@/components/ui';
import { showToast } from '@/lib/toast';
import { Loader } from '@/components/ui/Loader';
import InviteMemberModal from '@/pages/admin-users/modals/InviteMemberModal';
import BlockUnblockModal from '@/pages/admin-users/modals/BlockUnblockModal';
import DeleteUserModal from '@/pages/admin-users/modals/DeleteUserModal';
import { ProfileApiResponse } from '@/lib/api/services/admin-user';

// Helper function to generate avatar initials and color
const getAvatarData = (firstName: string, lastName: string) => {
    // Ensure both values are strings
    const first = String(firstName || '');
    const last = String(lastName || '');

    const initials = `${first[0] || ''}${last[0] || ''}`.toUpperCase();

    // Generate consistent color based on name
    const colors = [
        'bg-green-500 text-white',
        'bg-forestGreen-100 text-white',
        'bg-orange-500 text-white',
        'bg-purple-500 text-white',
        'bg-pink-500 text-white',
        'bg-indigo-500 text-white',
        'bg-yellow-500 text-white',
        'bg-red-500 text-white',
    ];

    const hash = (first + last).split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const colorIndex = hash % colors.length;

    return {
        initials: initials || '??',
        bgColor: colors[colorIndex]
    };
};

type AdminUser = ProfileApiResponse[0];
type TableAdminUser = AdminUser & {
    accountStatus?: string;
    isBlocked?: boolean;
    lastActiveAt?: string;
    lastActive?: string;
    invitedBy?: string;
    invitedByName?: string;
};

type MembersTab = 'MEMBERS' | 'PENDING';

const getRoleLabel = (role?: string) => {
    const normalizedRole = role?.toUpperCase();
    switch (normalizedRole) {
        case 'ADMIN':
            return 'Administrator';
        case 'ADMIN_STAFF':
            return 'Admin Staff';
        case 'EDITOR':
            return 'Editor';
        case 'VIEWER':
            return 'Viewer';
        default:
            return 'Viewer';
    }
};

const getRoleBadgeClassName = (role?: string) => {
    const normalizedRole = role?.toUpperCase();
    switch (normalizedRole) {
        case 'ADMIN':
        case 'ADMIN_STAFF':
            return 'bg-violet-100 text-violet-700';
        case 'EDITOR':
            return 'bg-blue-100 text-blue-700';
        case 'VIEWER':
        default:
            return 'bg-emerald-100 text-emerald-700';
    }
};

const getStatusData = (user: TableAdminUser) => {
    if (!user.hasActivatedAccount) {
        return {
            label: 'Pending',
            className: 'bg-amber-100 text-amber-700',
        };
    }

    const normalizedStatus = user.accountStatus?.toUpperCase();

    if (user.isBlocked || normalizedStatus?.includes('BLOCK') || normalizedStatus?.includes('SUSPEND')) {
        return {
            label: 'Suspended',
            className: 'bg-rose-100 text-rose-700',
        };
    }

    if (normalizedStatus?.includes('PENDING')) {
        return {
            label: 'Pending',
            className: 'bg-amber-100 text-amber-700',
        };
    }

    return {
        label: 'Active',
        className: 'bg-emerald-100 text-emerald-700',
    };
};

const getInvitedBy = (user: TableAdminUser) => {
    return user.invitedByName || user.invitedBy || 'System';
};


const AdminUsers: React.FC = () => {
    const navigate = useNavigate();
    const [isInviteModalOpen, setInviteModalOpen] = useState(false);
    const [isBlockUnblockModalOpen, setBlockUnblockModalOpen] = useState(false);
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<MembersTab>('MEMBERS');
    const [memberToBlockUnblock, setMemberToBlockUnblock] = useState<{ member: AdminUser; action: 'block' | 'unblock' } | null>(null);
    const [memberToDelete, setMemberToDelete] = useState<AdminUser | null>(null);

    // const { data: adminUsersData, isLoading, error, refetch } = useAdminUsers();
    const { data: currentUser } = useCurrentUser();
    const { data: currentUserProfile, isLoading, error, refetch  } = useCurrentUserProfile();
    const inviteAdminUserMutation = useInviteAdminUser();
    const toggleAccountStatusMutation = useToggleAccountStatus();
    const deleteAdminUserMutation = useDeleteAdminUser();

    const isAdminRole = useMemo(() => {
        const role = currentUser?.userRole?.toUpperCase();
        return role === 'ADMIN';
    }, [currentUser]);

    // Separate members (activated) from pending invites (not activated)
    const { members, pendingInvites } = useMemo(() => {
        const adminUsers = currentUserProfile?.data || [];
        const activated = adminUsers.filter((user: ProfileApiResponse[0]) => user.hasActivatedAccount);
        const pending = adminUsers.filter((user: ProfileApiResponse[0]) => !user.hasActivatedAccount);
        return { members: activated, pendingInvites: pending };
    }, [currentUserProfile]);

    const handleOpenBlockUnblockModal = (member: AdminUser, action: 'block' | 'unblock') => {
        setMemberToBlockUnblock({ member, action });
        setBlockUnblockModalOpen(true);
    };

    const handleConfirmBlockUnblock = async () => {
        if (!memberToBlockUnblock) return;

        const { member, action } = memberToBlockUnblock;

        try {
            await toggleAccountStatusMutation.mutateAsync({
                userId: member.userId,
                blockStatus: action === 'block'
            });
            setBlockUnblockModalOpen(false);
            setMemberToBlockUnblock(null);
            refetch();
        } catch (error) {
            console.error('Error toggling account status:', error);
            showToast.error(error instanceof Error ? error.message : 'Failed to update account status');
        }
    };

    const handleOpenDeleteModal = (member: AdminUser) => {
        setMemberToDelete(member);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!memberToDelete) return;

        try {
            await deleteAdminUserMutation.mutateAsync({
                userId: memberToDelete.userId,
                deleteStatus: true,
            });
            setDeleteModalOpen(false);
            setMemberToDelete(null);
            refetch();
        } catch (error) {
            console.error('Error deleting admin user:', error);
            showToast.error(error instanceof Error ? error.message : 'Failed to delete user');
        }
    };

    const handleInvite = async (data: { firstName: string; lastName: string; email: string; role: string }) => {
        try {
            await inviteAdminUserMutation.mutateAsync({
                userDto: {
                    firstName: data.firstName,
                    lastName: data.lastName,
                    emailAddress: data.email,
                    userRole: data.role
                }
            });
            showToast.success('Invitation sent successfully');
            setInviteModalOpen(false);
            refetch();
        } catch (error) {
            console.error('Error inviting admin user:', error);
            showToast.error(error instanceof Error ? error.message : 'Failed to send invitation. Please try again.');
        }
    };

    const handleRoleChange = (userId: string, newRole: string) => {
        // TODO: Implement role update API call when endpoint is available
        console.log('userId', userId);
        console.log('newRole', newRole);
        showToast.success('Role updated successfully');
        refetch();
    };

    const getDropdownActions = (member: AdminUser): DropdownAction[] => {
        const actions: DropdownAction[] = [
            {
                label: 'Block User',
                onClick: (e) => {
                    e.stopPropagation();
                    handleOpenBlockUnblockModal(member, 'block');
                },
                variant: 'default' as const
            },
            {
                label: 'Unblock User',
                onClick: (e) => {
                    e.stopPropagation();
                    handleOpenBlockUnblockModal(member, 'unblock');
                },
                variant: 'default' as const
            }
        ];

        if (isAdminRole) {
            actions.push({
                label: 'Delete User',
                onClick: (e) => {
                    e.stopPropagation();
                    handleOpenDeleteModal(member);
                },
                variant: 'danger' as const
            });
        }

        return actions;
    };

    const memberColumns: ColumnDef<AdminUser, unknown>[] = [
        {
            id: 'name',
            header: 'Staff Member',
            cell: ({ row }) => {
                const member = row.original as TableAdminUser;
                const { initials, bgColor } = getAvatarData(member.firstName, member.lastName);
                const fullName = `${member.firstName} ${member.lastName}`;

                return (
                    <div className="flex items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm mr-4 ${bgColor}`}>
                            {initials}
                        </div>
                        <div>
                            <div className="text-base font-semibold text-gray-900">{fullName}</div>
                            <div className="text-sm text-gray-500">{member.emailAddress}</div>
                        </div>
                    </div>
                );
            },
            meta: {
                headerClassName: 'text-left font-semibold uppercase tracking-wide',
                cellClassName: 'whitespace-nowrap',
            },
        },
        {
            id: 'role',
            header: 'Role',
            cell: ({row}) => {
                const member = row.original as TableAdminUser;
                return (
                    <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-sm font-semibold ${getRoleBadgeClassName(member.userRole)}`}>
                        {getRoleLabel(member.userRole)}
                    </span>
                );
            },
            meta: {
                headerClassName: 'text-left font-semibold uppercase tracking-wide',
                cellClassName: 'whitespace-nowrap',
            },
        },
        {
            id: 'status',
            header: 'Status',
            cell: ({ row }) => {
                const member = row.original as TableAdminUser;
                const status = getStatusData(member);

                return (
                    <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-sm font-semibold ${status.className}`}>
                        {status.label}
                    </span>
                );
            },
            meta: {
                headerClassName: 'text-left font-semibold uppercase tracking-wide',
                cellClassName: 'whitespace-nowrap',
            },
        },
        {
            id: 'invitedBy',
            header: 'Invited By',
            cell: ({ row }) => {
                const member = row.original as TableAdminUser;
                return <span className="text-sm text-gray-500">{getInvitedBy(member)}</span>;
            },
            meta: {
                headerClassName: 'text-left font-semibold uppercase tracking-wide',
                cellClassName: 'whitespace-nowrap',
            },
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: ({ row }) => {
                const member = row.original;
                return (
                    <div className="flex items-center justify-center">
                        {isAdminRole ? (
                            <div className="relative">
                                <select
                                    value={member.userRole || 'ADMIN_STAFF'}
                                    onChange={(e) => handleRoleChange(member.userId, e.target.value)}
                                    className="select-wine bg-white border border-gray-300 rounded-lg px-3 py-1.5 text-sm font-medium text-gray-700 pr-8 cursor-pointer"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <option value="ADMIN">Administrator</option>
                                    <option value="ADMIN_STAFF">Admin Staff</option>
                                    <option value="EDITOR">Editor</option>
                                    <option value="VIEWER">Viewer</option>
                                </select>
                            </div>
                        ) : null}
                        <ActionDropdown
                            id={member.userId}
                            actions={getDropdownActions(member)}
                        />
                    </div>
                );
            },
            meta: {
                headerClassName: 'text-center font-semibold uppercase tracking-wide',
                cellClassName: 'whitespace-nowrap',
            },
        },
    ];

    const pendingInviteColumns: ColumnDef<AdminUser, unknown>[] = [
        {
            id: 'name',
            header: 'Staff Member',
            cell: ({ row }) => {
                const invite = row.original as TableAdminUser;
                const { initials, bgColor } = getAvatarData(invite.firstName, invite.lastName);
                const fullName = `${invite.firstName} ${invite.lastName}`;

                return (
                    <div className="flex items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm mr-4 ${bgColor}`}>
                            {initials}
                        </div>
                        <div>
                            <div className="text-sm font-semibold text-gray-900">{fullName}</div>
                            <div className="text-sm text-gray-500">{invite.emailAddress}</div>
                        </div>
                    </div>
                );
            },
            meta: {
                headerClassName: 'text-left font-semibold uppercase tracking-wide',
                cellClassName: 'whitespace-nowrap',
            },
        },
        {
            id: 'role',
            header: 'Role',
            cell: ({ row }) => {
                const invite = row.original as TableAdminUser;
                return (
                    <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-sm font-semibold ${getRoleBadgeClassName(invite.userRole)}`}>
                        {getRoleLabel(invite.userRole)}
                    </span>
                );
            },
            meta: {
                headerClassName: 'text-left font-semibold uppercase tracking-wide',
                cellClassName: 'whitespace-nowrap',
            },
        },
        {
            id: 'status',
            header: 'Status',
            cell: ({ row }) => {
                const invite = row.original as TableAdminUser;
                const status = getStatusData(invite);
                return (
                    <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-sm font-semibold ${status.className}`}>
                        {status.label}
                    </span>
                );
            },
            meta: {
                headerClassName: 'text-left font-semibold uppercase tracking-wide',
                cellClassName: 'whitespace-nowrap',
            },
        },
        {
            id: 'invitedBy',
            header: 'Invited By',
            cell: ({ row }) => {
                const invite = row.original as TableAdminUser;
                return <span className="text-sm text-gray-500">{getInvitedBy(invite)}</span>;
            },
            meta: {
                headerClassName: 'text-left font-semibold uppercase tracking-wide',
                cellClassName: 'whitespace-nowrap',
            },
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: ({ row }) => {
                const invite = row.original;
                return (
                    <div className="flex items-center">
                        <ActionDropdown
                            id={invite.userId}
                            actions={getDropdownActions(invite)}
                        />
                    </div>
                );
            },
            meta: {
                headerClassName: 'text-left font-semibold uppercase tracking-wide',
                cellClassName: 'whitespace-nowrap',
            },
        },
    ];

    const activeTableData = activeTab === 'MEMBERS' ? members : pendingInvites;
    const activeTableColumns = activeTab === 'MEMBERS' ? memberColumns : pendingInviteColumns;
    const activeEmptyTitle = activeTab === 'MEMBERS' ? 'No Members Yet' : 'No Pending Invites';
    const activeEmptyDescription = activeTab === 'MEMBERS' ? 'You are yet to invite a member' : 'There are no pending invites at the moment';

    if (error) {
        return (
            <div className="text-center py-20">
                <p className="text-red-600">Error loading admin users: {error.message}</p>
            </div>
        );
    }

    return (
        <>
            <div className="space-y-8">
                <div className="space-y-6">
                    <div className="border-b border-gray-200 pb-4">
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-1 rounded-md bg-gray-100 p-1">
                                <button
                                    type="button"
                                    onClick={() => setActiveTab('MEMBERS')}
                                    className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${
                                        activeTab === 'MEMBERS'
                                            ? 'bg-wine-100 text-white'
                                            : 'text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    Members ({members.length})
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setActiveTab('PENDING')}
                                    className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${
                                        activeTab === 'PENDING'
                                            ? 'bg-wine-100 text-white'
                                            : 'text-gray-700 hover:bg-gray-200'
                                    }`}
                                >
                                    Pending Invites ({pendingInvites.length})
                                </button>
                            </div>

                            <Button variant="primary" onClick={() => setInviteModalOpen(true)}>
                                Invite Member
                            </Button>
                        </div>

                        {/* <div className="mt-3">
                            <h2 className="text-xl font-bold text-gray-800">
                                {activeTab === 'MEMBERS' ? 'Activated Members' : 'Pending Members'}
                            </h2>
                            <p className="text-sm text-gray-500 mt-1">
                                Invite your team members to work faster and collaborate together. Manage their permissions.
                            </p>
                        </div> */}
                    </div>

                    {isLoading ? (
                        <Loader size="md" variant="wine" message="Loading members..." />
                    ) : activeTableData.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="inline-block bg-gray-200/20 p-4 rounded-full">
                                <svg className="w-12 h-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m-7.5-2.962a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18 18.72v-2.172c0-.962.784-1.75 1.75-1.75h.5c.966 0 1.75.788 1.75 1.75v2.172M12.121 12.121a3 3 0 10-4.242 0 3 3 0 004.242 0zM19.5 12c0 .245-.02.486-.058.722m-2.146-4.322a3 3 0 014.243 4.242" />
                                </svg>
                            </div>
                            <h3 className="mt-4 text-md font-semibold text-gray-800">{activeEmptyTitle}</h3>
                            <p className="mt-1 text-sm text-gray-500">{activeEmptyDescription}</p>
                            <Button
                                variant="primary"
                                onClick={() => setInviteModalOpen(true)}
                                className="mt-6"
                            >
                                Invite Member
                            </Button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <TanStackTable
                                data={activeTableData}
                                columns={activeTableColumns}
                                getRowId={(row) => row.userId}
                                onRowClick={(row) => navigate(`/admin/admin-users/${row.userId}`)}
                                className="min-w-full border border-gray-200 overflow-hidden rounded-md shadow-sm"
                                tableClassName="min-w-full divide"
                               theadClassName="bg-white border-b border-gray-200"
                                tbodyClassName="bg-white divide-y divide-gray-200 "
                            />
                        </div>
                    )}
                </div>
            </div>

          {isInviteModalOpen && (
            <InviteMemberModal
                isOpen={isInviteModalOpen}
                onClose={() => setInviteModalOpen(false)}
                onInvite={handleInvite}
                isInviting={inviteAdminUserMutation.isPending}
            />)}

            {isBlockUnblockModalOpen && memberToBlockUnblock && (
                <BlockUnblockModal
                    isOpen={isBlockUnblockModalOpen}
                    member={memberToBlockUnblock.member}
                    action={memberToBlockUnblock.action}
                    onClose={() => setBlockUnblockModalOpen(false)}
                    onConfirm={handleConfirmBlockUnblock}
                    isProcessing={toggleAccountStatusMutation.isPending}
                />
            )}

            {isDeleteModalOpen && memberToDelete && (
                <DeleteUserModal
                    isOpen={isDeleteModalOpen}
                    member={memberToDelete}
                    onClose={() => {
                        setDeleteModalOpen(false);
                        setMemberToDelete(null);
                    }}
                    onConfirm={handleConfirmDelete}
                    isProcessing={deleteAdminUserMutation.isPending}
                />
            )}
        </>
    );
};

export default AdminUsers;
